import { FC } from 'react'
import { Button, Input, RequiredAsterisk, Select } from 'src/components'
import { CAMPAIGN_QUESTION_TYPE_OPTIONS } from 'src/constants'
import { EJobQuestionType } from 'src/enums'
import { ErrorBag } from 'src/hooks'
import { IconAdd, IconClose, IconTrash } from 'src/icons'
import { IApplicationQuestionsModel } from 'src/interfaces/models/campaign-question.model'
import { ensureArray } from 'src/utils'
import Style from './style.module.scss'

interface IProps {
  loading?: boolean
  applicationQuestions?: IApplicationQuestionsModel[]
  onChange?: (values: IApplicationQuestionsModel[]) => void
  errors?: ErrorBag<IApplicationQuestionsModel[]>
}

export const CampaignQuestion: FC<IProps> = ({ loading, applicationQuestions = [], onChange, errors }) => {
  const addNewQuestion = () => {
    const newQuestions = {
      content: '',
      type: EJobQuestionType.MULTIPLE_CHOICE,
      options: [{
        content: ''
      }]
    }

    onChange?.([...applicationQuestions, newQuestions])
  }

  const removeQuestion = (index: number) => {
    onChange?.(ensureArray(applicationQuestions?.filter((_, i) => i !== index)))
  }

  const addNewOption = (question: IApplicationQuestionsModel, index: number) => {
    const newQuestion = { ...question, options: [...ensureArray(question.options), ''] }
    onChange?.(ensureArray(applicationQuestions?.map((q, i) => i === index ? newQuestion : q)))
  }

  const removeOption = (question: IApplicationQuestionsModel, questionIndex: number, optionIndex: number) => {
    const newQuestion = { ...question, options: question.options?.filter((_, i) => i !== optionIndex) }
    onChange?.(ensureArray(applicationQuestions?.map((q, i) => i === questionIndex ? newQuestion : q)))
  }

  const changeQuestion = (value: IApplicationQuestionsModel, index: number) => {
    onChange?.(ensureArray(applicationQuestions?.map((q, i) => i === index ? { ...q, ...value } : q)))
  }

  return (
    <div className="p-4 fx-column gap-8">
      {applicationQuestions?.map((question, index) => (
        <div key={index} className="fx-column gap-4">
          <div className="fx-column gap-2">
            <div className="fx fx-ai-center fx-jc-space-between gap-4">
              <div className="fw-700 txt-black-01 lh-20">
                Question<RequiredAsterisk/>
              </div>
              <IconTrash color="red" className="pointer" onClick={() => removeQuestion(index)}/>
            </div>

            <Input
              name="question"
              placeholder="Ask a brief question"
              disabled={loading}
              value={question.content}
              onChange={(e) => changeQuestion({ content: e.target.value }, index)}
              className="py-1"
              error={errors?.getError(`applicationQuestions[${index}].content`)}
            />
          </div>

          <div key={index} className="fx-column gap-4">
            <div className="fx-column gap-2">
              <div className="fw-700 txt-black-01 lh-20">
                Question Type<RequiredAsterisk/>
              </div>
              <Select
                height={48}
                className="w-full"
                value={question.type}
                options={CAMPAIGN_QUESTION_TYPE_OPTIONS}
                isSearchable={false}
                isDisabled={loading}
                onChange={(value) => changeQuestion({ type: value as EJobQuestionType }, index)}
              />
            </div>

            {question.type === EJobQuestionType.LINK
              ? (
                <div className="fx-column gap-3">
                  <div className="fx fx-ai-center gap-5">
                    <div className="fw-500 txt-grey-04" style={{ minWidth: 88 }}>
                      URL<RequiredAsterisk/>
                    </div>

                    <Input
                      name="url"
                      disabled={loading}
                      placeholder="Enter URL"
                      value={question.url}
                      onChange={(e) => changeQuestion({ url: e.target.value }, index)}
                      className="py-1"
                      wrapperClassName="w-full"
                      error={errors?.getError(`applicationQuestions[${index}].url`)}
                    />
                  </div>

                  <div className="fx fx-ai-center gap-5">
                    <div className="fw-500 txt-grey-04" style={{ minWidth: 88 }}>
                      Display Text
                    </div>

                    <Input
                      name="displayText"
                      disabled={loading}
                      placeholder="Enter display text for the URL"
                      value={question.displayText}
                      onChange={(e) => changeQuestion({ displayText: e.target.value }, index)}
                      className="py-1"
                      wrapperClassName="w-full"
                    />
                  </div>
                </div>
              )
              : (
                <div className="fx-column gap-3">
                  {question.options?.map((option, optionIndex) => (
                    <div key={optionIndex} className="fx fx-ai-center gap-3 w-full">
                      {question.type === EJobQuestionType.MULTIPLE_CHOICE
                        ? (
                          <input
                            type="radio"
                            className={Style.radioInput}
                          />
                        )
                        : (
                          <input
                            type="checkbox"
                            className={Style.radioInput}
                          />
                        )}

                      <Input
                        name="option"
                        disabled={loading}
                        placeholder={`Option ${optionIndex + 1}`}
                        value={option?.content}
                        onChange={(e) => changeQuestion({ options: ensureArray(question.options).map((o, i) => i === optionIndex ? { content: e.target.value } : o) }, index)}
                        className="py-1"
                        wrapperClassName="w-full"
                      />

                      {(question.options?.length || 0) > 1 && (
                        <IconClose size={20} color="#0C0C0C" className="pointer" onClick={() => removeOption(question, index, optionIndex)}/>
                      )}
                    </div>
                  ))}

                  {(question.options?.length || 0) < 5 && (
                    <div className="mt-2 fx fx-ai-center gap-2 fs-12 fw-500 txt-blue-1000 pointer w-fit" onClick={() => addNewOption(question, index)}>
                      <IconAdd className="txt-blue-1000"/>
                      Add Option
                    </div>
                  )}
                </div>
              )}

          </div>
        </div>
      ))}

      {applicationQuestions.length < 10 && (
        <Button variant="secondary" className="w-fit" onClick={addNewQuestion}>
          + Add Question
        </Button>
      )}
    </div>
  )
}
